import { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';

import { siteboardAction } from '../actions/defaultActions';
import { GET_PROJECT_CONTRACTORS, SET_EDIT_CONTRACTORS_DIALOGUE_ID, SET_PROJECT_CONTRACTORS, ContractorName, SiteboardApiValidator } from '../actions/types';
import PopupWindow from './PopupWindow';

import Logo from '../SiteBoard_Logo';
import AddIcon from '../icons/addIcon';
import TrashIcon from '../icons/trashIcon';
import ExpandDownIcon from '../icons/expandDownIcon';

const EditContractorsDialogue = ({ _userId, _projects, _subscriptions, _contractorList, _editContractorsDialogueId, _pendingDataRequests, siteboardAction }) => {
  const [contractorList, setContractorList] = useState(_editContractorsDialogueId in _contractorList ? _contractorList[_editContractorsDialogueId].contractors : []);
  const [selectedContractorIndex, setSelectedContractorIndex] = useState(null);
  const [newLineIndex, setNewLineIndex] = useState(null);
  const [moveRowUpEnabled, setMoveRowUpEnabled] = useState(false);
  const [moveRowDownEnabled, setMoveRowDownEnabled] = useState(false);
  const [isLoadingContractorList, setIsLoadingContractorList] = useState(true);
  const [loadContractorListFailureMessage, setLoadContractorListFailureMessage] = useState(null);

  const [errorMessage, setErrorMessage] = useState(null);
  const [errorIndex, setErrorIndex] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    const onSuccess = () => { 
      setContractorList(_contractorList[_editContractorsDialogueId].contractors); 
      setIsLoadingContractorList(false);
    };
    const onError = (message) => { setLoadContractorListFailureMessage(message); };
    siteboardAction(GET_PROJECT_CONTRACTORS, {projectId: _editContractorsDialogueId}, onSuccess, onError);
  }, [_contractorList, _editContractorsDialogueId, siteboardAction]);

  // Check the contractor list state against the stored contractor list and enable the save changes button if they are different
  useEffect(() => {
    const storedList = _editContractorsDialogueId in _contractorList ? _contractorList[_editContractorsDialogueId].contractors : []

    if(storedList.length !== contractorList.length) {
      setHasUnsavedChanges(true);
      return;
    }

    for(let i = 0; i < contractorList.length; i++) {
      if(contractorList[i] !== storedList[i]) {
        setHasUnsavedChanges(true);
        return;
      }
    }

    setHasUnsavedChanges(false);
  }, [contractorList, _contractorList, _editContractorsDialogueId]);
  
  // Deselect activity if mouse is clicked anywhere outside it
  // TODO: do this for touch devices too
  useEffect(() => {
    const onMouseDown = (event) => {
     try {
       let element = event.target;
       do {
         if(typeof element.className === 'string' && element.className.indexOf('contractorInput') >= 0) return;
         if(typeof element.className === 'string' && element.className.indexOf('contractorListButton') >= 0) return;
         element = element.parentNode;
       } while(element.tagName !== 'body' && element.tagName !== 'BODY');
       setSelectedContractorIndex(null);
     } catch(error) {}
    };

    window.addEventListener('mousedown', onMouseDown);
    return () => { // remove listener when component unloads
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  useEffect(() => {
    setMoveRowUpEnabled(selectedContractorIndex !== null && selectedContractorIndex > 0);
    setMoveRowDownEnabled(selectedContractorIndex !== null && selectedContractorIndex < contractorList.length - 1);
  }, [selectedContractorIndex, contractorList]);

  const selectedProject = _editContractorsDialogueId in _projects ? _projects[_editContractorsDialogueId] : null;
  const selectedSubscription = selectedProject !== null ? _subscriptions[selectedProject.subscriptionId] : null;
  if(selectedProject === null) return (<></>);

  const userCanEditContractors = () => {
    return SiteboardApiValidator[SET_PROJECT_CONTRACTORS].isUserIdAuthorized(_userId, selectedSubscription, selectedProject)
  };

  const closeDialogue = () => {
    siteboardAction(SET_EDIT_CONTRACTORS_DIALOGUE_ID, {projectId: '00000000-0000-0000-0000-000000000000'});
  };

  const onAddContractor = () => {
    setErrorIndex(null);
    setErrorMessage(null);
    setSelectedContractorIndex(contractorList.length);
    setNewLineIndex(contractorList.length);
    setContractorList([...contractorList, '']);
  };

  const onMoveRowUp = () => {
    setErrorIndex(null);
    setErrorMessage(null);

    const newContractorList = [];
    let index = 0;
    while(index < contractorList.length) {
      if((index+1) < contractorList.length && (index+1) === selectedContractorIndex) {
        newContractorList.push(contractorList[index+1]);
        newContractorList.push(contractorList[index]);
        index += 2;
      } else {
        newContractorList.push(contractorList[index]);
        index++;
      }
    }
    setContractorList(newContractorList);
    setSelectedContractorIndex(selectedContractorIndex-1);
  };

  const onMoveRowDown = () => {
    setErrorIndex(null);
    setErrorMessage(null);

    const newContractorList = [];
    let index = 0;
    while(index < contractorList.length) {
      if((index+1) < contractorList.length && index === selectedContractorIndex) {
        newContractorList.push(contractorList[index+1]);
        newContractorList.push(contractorList[index]);
        index += 2;
      } else {
        newContractorList.push(contractorList[index]);
        index++;
      }
    }
    setContractorList(newContractorList);
    setSelectedContractorIndex(selectedContractorIndex+1);
  };

  const onDeleteContractor = () => {
    setErrorIndex(null);
    setErrorMessage(null);

    if (selectedContractorIndex === null) return;

    const newContractorList = [...contractorList];
    newContractorList.splice(selectedContractorIndex, 1);
    setContractorList(newContractorList);
    setSelectedContractorIndex(null);
  };

  const onSaveChanges = () => {
    for(let i = 0; i < contractorList.length; i++) {
      try {
        const name = ContractorName.validate(contractorList[i]);
      } catch(error) {
        setErrorMessage(error.message);
        setErrorIndex(i);
        setSelectedContractorIndex(i);
        return;
      }
    }

    const params = {
      contractorList: {
        projectId: _editContractorsDialogueId,
        contractors: contractorList.filter((item) => item.length > 0), // filter out empty rows
        createdTimestamp: new Date().getTime(), // this will be overwritten by the server
        modifiedTimestamp: new Date().getTime(), // this will be overwritten by the server
      },
      subscriptionId: selectedProject.subscriptionId,
    };
    siteboardAction(SET_PROJECT_CONTRACTORS, params, (output) => {
      closeDialogue();
    });
  }

  return (
    <PopupWindow title={(userCanEditContractors() ? 'Edit' : '') + ' Contractor List'} closeDialogue={closeDialogue} bgStyle='bg-[#333333]'>
      <div className='flex flex-col gap-y-[30px] max-w-[400px] text-white'>
        {isLoadingContractorList &&
          <div className='grow flex flex-col justify-center gap-y-1 p-1'>
            <div className='flex rounded-[10px] py-6 px-10 items-center justify-center font-semibold text-white text-[1.5em] gap-x-4'>
              <div className='animate-[spin_1s_linear_infinite]'>
                <Logo size={48} />
              </div>
              <p className='select-none'>Loading Contractor List</p>
            </div>
          </div>
        }
        {!isLoadingContractorList &&
        <>
          <div>These names will appear in the Contractor Name dropdown list for your daily report worklog items.</div>
          {/* list control buttons */}
          {userCanEditContractors() &&
            <div className='flex justify-between gap-x-1'>
              {/* Add contractor button */}
              <div className='flex justify-start'>
                <button
                  className='contractorListButton flex items-center bg-[#a29c9c] hover:bg-[#efe4e4] text-black font-semibold select-none py-[6px] px-[5px] sm:px-[10px] gap-x-[5px] sm:gap-x-[12px] whitespace-nowrap'
                  onClick={onAddContractor}
                >
                  <AddIcon />
                  <p className=''>Add Row</p>
                </button>
              </div>
              {/* Up/Down button */}
              <div className='flex justify-center gap-x-2'>
                {/* Move row up button */}
                <button 
                  className={'flex contractorListButton items-center font-semibold select-none px-[10px] py-[6px] gap-x-[4px] whitespace-nowrap bg-[#a29c9c] text-black ' + (!moveRowUpEnabled ? 'cursor-not-allowed opacity-30' : 'hover:bg-[#efe4e4]')}
                  title='Move up selected row'
                  disabled={!moveRowUpEnabled}
                  onClick={onMoveRowUp}
                >
                  <div className='scale-y-[-1]'>
                    <ExpandDownIcon color='black'/>
                  </div>
                </button>
                {/* Move row down button */}
                <button 
                  className={'flex contractorListButton items-center font-semibold select-none px-[10px] py-[6px] gap-x-[4px] whitespace-nowrap bg-[#a29c9c] text-black ' + (!moveRowDownEnabled ? 'cursor-not-allowed opacity-30' : 'hover:bg-[#efe4e4]')}
                  title='Move down selected row'
                  disabled={!moveRowDownEnabled}
                  onClick={onMoveRowDown}
                >
                  <ExpandDownIcon color='black'/>
                </button>
              </div>
              {/* Delete contractor button */}
              <button
                className={'contractorListButton flex items-center font-semibold text-white select-none px-[10px] py-[6px] gap-x-[4px] bg-[#cc1b2d] hover:bg-[#f12035] whitespace-nowrap ' + (selectedContractorIndex === null ? 'cursor-not-allowed opacity-30' : '')}
                title='Delete selected row'
                disabled={selectedContractorIndex === null}
                onClick={onDeleteContractor}
              >
                <TrashIcon color='white' />
                <p>Delete Row</p>
              </button>
            </div>
          }
          {/* list of contractors */}
          <div className='flex flex-col overflow-y-auto h-[25vh] sm:h-[40vh]'>
            {contractorList.length === 0 &&
              <div className='font-bold text-[1.1em]'>Your list of contractor names is empty</div>
            }
            {contractorList.length > 0 &&
              <div className='grid grid-cols-[1fr_12fr] gap-y-2 mx-2'>
              {contractorList.map((item, index) => (
                <ContractorLine
                  key={index}
                  isNew={index === newLineIndex}
                  setNewLineIndex={setNewLineIndex}
                  isSelected={index === selectedContractorIndex}
                  errorIndex={errorIndex}
                  index={index}
                  selectedContractorIndex={selectedContractorIndex}
                  contractorList={contractorList}
                  userCanEditContractors={userCanEditContractors}
                  setErrorIndex={setErrorIndex}
                  setErrorMessage={setErrorMessage}
                  setContractorList={setContractorList}
                  setSelectedContractorIndex ={setSelectedContractorIndex}
                />
              ))}
              </div>
            }
          </div>
          {/* save button  */}
          {userCanEditContractors() &&
            <div className='flex flex-col justify-center'>
              <div className='flex justify-center'>
                <button  
                  disabled={!hasUnsavedChanges}
                  title={hasUnsavedChanges ? '' :'You have no unsaved changes'}
                  className={(hasUnsavedChanges ? 'hover:bg-[#603aff]' : 'cursor-not-allowed opacity-30') + ' bg-[#471cff] text-white py-[7px] px-[42px] font-bold select-none rounded-[10px]' }
                  onClick={onSaveChanges}
                >
                  Save Changes
                </button>
              </div>
              <div className={`flex justify-center text-white leading-[2em] ${hasUnsavedChanges ? 'opacity-100' : 'opacity-0'}`}>
                <p className='max-w-[280px]'>You have unsaved changes</p>
              </div>
              {typeof errorMessage === 'string' && 
                <div className='flex justify-center text-[#f12035] font-bold mt-1'>
                  <p className='max-w-[280px]'>{errorMessage}</p>
                </div>
              }
            </div>
          }
        </>
        }
      </div>
    </PopupWindow>
  );
};

const ContractorLine = ({ errorIndex, index, selectedContractorIndex, contractorList, userCanEditContractors, setErrorIndex, setErrorMessage, setContractorList, setSelectedContractorIndex, isNew, setNewLineIndex, isSelected }) => {
  const inputRef = useRef(null); 
  const hasInputFocus = useRef(false);

  // focus input when newly created activity is shown
  useEffect(() => {
    if(isNew === true) {
      //inputRef.current.focus();
      try { setNewLineIndex(null); } catch(error) {} // set the line as no longer new
    }
  }, [isNew, setNewLineIndex]);

  // bring activity into view if it is selected, if it has been moved up/down it can move out of view
  // this gets triggered when the activity line rows are reordered after moving a row up/down
  useEffect(() => {
    if (isSelected !== true) return;
    if(hasInputFocus.current === true) return; // if it has focus it is already in view
    inputRef.current.focus();
    inputRef.current.blur();
  }, [isSelected]);

  return (
  <>
    <div className={errorIndex === index ? 'text-red-500 font-bold' : ''}>{index+1}.</div>
    <div className={`w-full h-[1.7em] my-0.5 contractorInput flex items-center`}>
      <input 
        ref={inputRef}
        className={'w-full h-full px-2 rounded-[5px] outline-none ' + (errorIndex === index ? ' bg-red-500 ' : (selectedContractorIndex === index ? ' bg-[#656565] ' : ' bg-[#444247] hover:bg-[#656565] text-white '))}
        type='text' 
        placeholder='contractor name'
        disabled={!userCanEditContractors()}
        value={contractorList[index]}
        onFocus={() => {
          hasInputFocus.current = true;
          try { setSelectedContractorIndex(index); } catch(error) {} }}
        onTouchStart = {() => {}}
        onBlur={() => {
          hasInputFocus.current = false;
        }}
        onChange={(event) => {
          setErrorIndex(null);
          setErrorMessage(null);
          const newContractorList = [...contractorList];
          newContractorList[index] = event.target.value;
          setContractorList(newContractorList);
        }}
       />
    </div>
  </>
  );
};

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _userId: state.localPersistent.userId,
  _projects: state.serverSynced.projects,
  _contractorList: state.serverSynced.contractorList,
  _pendingDataRequests: state.localEphemeral.pendingDataRequests,
  _editContractorsDialogueId: state.localEphemeral.editContractorsDialogueId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, { siteboardAction })(EditContractorsDialogue);
